// HomeHub waitlist / notify-me form.
// POSTs the signup to the FamilyWall endpoint (data-endpoint) and shows a
// success or error state based on the response. Also dispatches a
// `hh:waitlist:submit` DOM event so any in-page listener can react without
// depending on this code. Custom element upgrades each <hh-waitlist> once.

class HHWaitlist extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this.form = this.querySelector('.hh-waitlist__form');
    this.input = this.querySelector('.hh-waitlist__input');
    this.button = this.querySelector('.hh-waitlist__button');
    this.message = this.querySelector('.hh-waitlist__message');
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }

  onSubmit(event) {
    event.preventDefault();
    const email = (this.input.value || '').trim();

    if (!this.isValidEmail(email)) {
      this.showMessage('Please enter a valid email address.', 'error');
      this.input.focus();
      return;
    }

    this.setBusy(true);
    this.clearMessage();

    const utm = this.readUtms();
    const payload = {
      email: email,
      screenSize: this.dataset.variantTitle || '',
      price: this.dataset.price || '',
      // Which market the buyer is in (us / europe / uk), so the endpoint can
      // notify them when HomeHub launches in their market. Sent under the
      // existing `language` key to keep the endpoint contract stable.
      language: this.dataset.market || '',
      UTM_SOURCE: utm.source,
      UTM_MEDIUM: utm.medium,
      UTM_CAMPAIGN: utm.campaign,
    };

    const endpoint = this.dataset.endpoint || 'https://api.familywall.com/homehub';

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => this.parseJsonBody(response).then((body) => ({ response, body })))
      .then(({ response, body }) => {
        // The endpoint signals a handled error (e.g. duplicate email) via an
        // `error` message in the JSON body, which can accompany any status.
        // Prefer that message; otherwise fall back to the generic error for a
        // non-2xx, or treat it as success.
        const serverError = body && typeof body.error === 'string' ? body.error : null;
        if (serverError) {
          this.onError(payload, serverError, body);
        } else if (!response.ok) {
          this.onError(payload, null, body);
        } else {
          this.onSuccess(payload);
        }
      })
      .catch((err) => {
        // Network failure / request never completed.
        this.onError(payload, null, null, err);
      });
  }

  // Parse the response body as JSON only when the server says it's JSON.
  // A non-JSON body (HTML error page, gateway block) resolves to null so we
  // fall back to the generic message instead of throwing on response.json().
  parseJsonBody(response) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') === -1) {
      return Promise.resolve(null);
    }
    return response.json().catch(() => null);
  }

  onSuccess(payload) {
    this.setBusy(false);
    this.form.reset();
    this.showMessage("You're on the list. We'll email you soon.", 'success');
    this.dispatchEvent(
      new CustomEvent('hh:waitlist:submit', {
        bubbles: true,
        detail: { ok: true, payload },
      })
    );
  }

  // message: server-provided error string (preferred), or null to use the
  // generic fallback. body/err are passed through on the DOM event for listeners.
  onError(payload, message, body, err) {
    this.setBusy(false);
    this.showMessage(message || 'Something went wrong. Please try again.', 'error');
    this.dispatchEvent(
      new CustomEvent('hh:waitlist:submit', {
        bubbles: true,
        detail: {
          ok: false,
          payload,
          error: message || (err ? String(err) : null),
          errorCode: body && typeof body.errorCode !== 'undefined' ? body.errorCode : null,
        },
      })
    );
  }

  // UTM values come from the current URL; missing params default to '' to match
  // the endpoint's expected payload shape.
  readUtms() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      params = null;
    }
    const get = (key) => (params ? params.get(key) || '' : '');
    return {
      source: get('utm_source'),
      medium: get('utm_medium'),
      campaign: get('utm_campaign'),
    };
  }

  isValidEmail(value) {
    // Simple, permissive check; server/Segment is the source of truth.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  setBusy(isBusy) {
    if (isBusy) {
      this.button.setAttribute('aria-busy', 'true');
    } else {
      this.button.removeAttribute('aria-busy');
    }
  }

  showMessage(text, kind) {
    this.message.textContent = text;
    this.message.classList.remove(
      'hh-waitlist__message--error',
      'hh-waitlist__message--success'
    );
    if (kind) this.message.classList.add('hh-waitlist__message--' + kind);
  }

  clearMessage() {
    this.showMessage('', null);
  }
}

if (!customElements.get('hh-waitlist')) {
  customElements.define('hh-waitlist', HHWaitlist);
}
